/* ==========================================================================
   BYHIMSELF - Motion Edition
   Loaded by sections/bh-ux-boot.liquid, after bh-ux.js.

   Almost everything in this upgrade is CSS: Dawn's own scroll-trigger system
   handles section reveals (retuned in bh-motion.css), scroll-driven CSS
   animations handle the hero and the style gallery, and @view-transition
   handles page transitions. None of that needs a frame loop.

   What is left needs the DOM, and only that lives here:
     1. the bh-js flag, so JS-only styles never hide content when JS fails
     2. a sentinel that tells the header it is no longer at the top
     3. splitting the hero headline into words

   There is deliberately no scroll listener and no requestAnimationFrame loop.
   ========================================================================== */

(() => {
  'use strict';

  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* 1. Anything that hides content is gated behind this class, which only
     exists once the script has run. If the script never runs, or throws before
     this line, the page stays fully visible and purchasable. */
  root.classList.add('bh-js');
  if (reduced) root.classList.add('bh-reduced');

  /* 2. Header condense.
     A sentinel at the top of the document: while it is in view we are at the
     top of the page. An IntersectionObserver costs nothing between crossings,
     unlike a scroll handler that fires on every frame. */
  function headerSentinel() {
    const header = document.querySelector('.section-header, sticky-header');
    if (!header) return;

    const apply = (on) => root.classList.toggle('bh-scrolled', on);

    /* Seed from the real scroll position first, so a restored scroll position
       is right on the first frame rather than after the observer fires. */
    apply(window.scrollY > 40);

    const mark = document.createElement('div');
    mark.setAttribute('aria-hidden', 'true');
    /* display:block is forced because a theme rule was collapsing this element
       to display:none - a zero-area target never intersects, so the observer
       reported "scrolled" on a page sitting at the top. Inline + !important is
       the only way to be certain from a script that ships before the CSS it
       has to beat. visibility:hidden would do the same thing, so the element is
       simply made empty and unclickable instead. */
    mark.style.cssText =
      'display:block !important;position:absolute;top:0;left:0;width:100%;height:48px;' +
      'pointer-events:none;opacity:0;z-index:-1';
    document.body.prepend(mark);

    new IntersectionObserver(([entry]) => apply(!entry.isIntersecting), {
      threshold: 0,
    }).observe(mark);
  }

  /* 3. Hero headline, word by word.
     The original text is kept as a single visually-hidden node and the split
     copy is hidden from assistive tech, so screen readers and copy-paste get
     one continuous sentence rather than a stack of fragments. */
  function splitHeadline() {
    if (reduced) return;

    const el = document.querySelector('.banner--large .banner__heading');
    if (!el || el.dataset.bhSplit === 'done') return;

    const text = el.textContent.trim();
    if (!text || text.length > 120) return; // long headings read badly word-split

    const words = text.split(/\s+/);
    if (words.length > 14) return;

    el.dataset.bhSplit = 'done';
    el.textContent = '';

    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = text;
    el.appendChild(sr);

    const shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    shown.className = 'bh-split';
    words.forEach((w, i) => {
      const word = document.createElement('span');
      word.className = 'bh-split__w';
      word.style.setProperty('--i', i);
      word.textContent = w;
      shown.appendChild(word);
      if (i < words.length - 1) shown.appendChild(document.createTextNode(' '));
    });
    el.appendChild(shown);
  }

  const start = () => {
    try {
      headerSentinel();
      splitHeadline();
    } catch (e) {
      // Never let a motion failure take the page with it.
      root.classList.remove('bh-js');
      console.warn('[bh-motion]', e);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
